import type {ReactElement} from 'react';
import React from 'react';
import type {RGBA} from '@Utils/color.util';
import {rgbaToHexString} from '@Utils/color.util';
import {PMW_COLORS_NEUTRAL} from '@Utils/color.util';
import styles from './color-fill.module.scss';

interface ColorFillProps {
  id?: string;
  color: RGBA | RGBA[];
  className?: string;
  height?: number;
  width?: number;
  hasFocus?: boolean;
  colorFillPropertyType?: ColorFillPropertyType;
  isDisabled?: boolean;
  isSelected?: boolean;

  onClick?(id?: string): void;
}

export enum ColorFillPropertyType {
  SOLID = 'SOLID',
  RADIAL = 'RADIAL',
  LINEAR = 'LINEAR',
}

const DEFAULT_COLOR_FILL_WIDTH = 50;
const DEFAULT_COLOR_FILL_HEIGHT = 30;

export function ColorFill({
  id = '',
  className = '',
  height = DEFAULT_COLOR_FILL_HEIGHT,
  width = DEFAULT_COLOR_FILL_WIDTH,
  hasFocus = true,
  onClick = (): void => {},
  colorFillPropertyType = ColorFillPropertyType.SOLID,
  isDisabled = false,
  isSelected = false,
  ...props
}: ColorFillProps): ReactElement<any> | null {
  const isArray = (): boolean => {
    return Array.isArray(props.color);
  };

  const getBackground = (): string => {
    if (isDisabled) {
      return PMW_COLORS_NEUTRAL.NEUTRAL_11;
    }
    switch (colorFillPropertyType) {
      case ColorFillPropertyType.SOLID:
        return rgbaToHexString(props.color as RGBA);
      case ColorFillPropertyType.LINEAR:
        if (isArray()) {
          const color1 = rgbaToHexString(props.color[1] as RGBA);
          const color2 = rgbaToHexString(props.color[0] as RGBA);
          return `linear-gradient(${color1}, ${color2})`;
        }
        return '';

      case ColorFillPropertyType.RADIAL:
        if (isArray()) {
          const color1 = rgbaToHexString(props.color[1] as RGBA);
          const color2 = rgbaToHexString(props.color[0] as RGBA);
          return `radial-gradient(${color1}, ${color2})`;
        }
        return '';

      default:
        return '';
    }
  };

  return (
    <span
      onClick={(): void => {
        if (isDisabled) {
          return;
        }
        onClick(id);
      }}
      id={id}
      style={{
        background: getBackground(),
        backgroundImage: getBackground(),
        width,
        height,
      }}
      className={`${className} ${styles.container} ${isDisabled ? styles.disabled : ''} ${isSelected ? styles.selected : ''} ${hasFocus && !isDisabled ? '' : styles.noFocus}`}
    />
  );
}
